import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { filter } from 'rxjs/operators';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private measurementId: string = '';
  private isLoaded: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private titleService: Title
  ) {}

  /**
   * Initialisiert Google Analytics 4 mit der angegebenen Measurement ID
   */
  public initialize(measurementId: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return; // Nicht im Server-Side-Rendering ausführen
    }

    this.measurementId = measurementId;

  }

  /**
   * Lädt das GA4-Skript in den DOM
   */
  public loadGa4(): void {
    if (!isPlatformBrowser(this.platformId) || !this.measurementId || this.isLoaded) {

      return;
    }

    // GA4-Basisfunktionen initialisieren
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };
    
    // Initial gtag-Aufruf
    window.gtag('js', new Date());
    window.gtag('config', this.measurementId, {
      send_page_view: false // Wir werden Page-Views manuell senden
    });
    


    // GA4-Skript laden
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
    
    script.onload = () => {
      this.isLoaded = true;
      
      // Router-Events für Seitenaufrufe einrichten
      this.setupPageTracking();
    };
    
    script.onerror = (error) => {
      console.error('Analytics Service: Fehler beim Laden des GA4-Skripts:', error);
    };
    
    document.head.appendChild(script);

  }

  /**
   * Richtet das Tracking von Seitenaufrufen ein
   */
  private setupPageTracking(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      if (this.isLoaded) {
        const pageTitle = this.titleService.getTitle();
        const pagePath = event.urlAfterRedirects;
        
        window.gtag('event', 'page_view', {
          page_title: pageTitle,
          page_path: pagePath,
          page_location: window.location.href
        });
        

      }
    });
  }

  /**
   * Entfernt das GA4-Skript aus dem DOM
   */
  public removeGa4(): void {
    if (!isPlatformBrowser(this.platformId) || !this.isLoaded) {
      return;
    }

    // GA4-Skript entfernen
    const scripts = document.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; i++) {
      const script = scripts[i];
      if (script.src.includes(`gtag/js?id=${this.measurementId}`)) {
        script.remove();
        break;
      }
    }

    this.isLoaded = false;

  }

  /**
   * Sendet ein Event an GA4
   */
  public sendEvent(eventName: string, eventParams?: Record<string, any>): void {
    if (!isPlatformBrowser(this.platformId) || !this.isLoaded) {
      return;
    }

    window.gtag('event', eventName, eventParams);

  }
}